/**
 * @fileoverview Avatar Storage Utility
 * @active Used for managing user avatars in Supabase Storage
 *
 * This utility provides centralized methods for managing user avatars,
 * using Supabase Storage for file operations.
 *
 * @see {@link src/app/utils/supabase/client.ts} - Supabase client utilities
 * @see {@link src/services/profile.service.ts} - Profile service that uses these utilities
 * @see {@link src/types/userAuth.ts} - User type definitions
 * @see {@link src/services/profile.server.ts} - Server actions for avatar operations
 */

import { createClientComponent } from "@/app/utils/supabase/client";
import { createErrorObject, handleRTKError } from "@/types/userAuth";
import type { UserProfile } from "@/types/userAuth";
import {
  getAvatarUrlAction,
  getUserProfileAction,
  updateUserAvatarAction,
  deleteAvatarFileAction,
} from "@/services/profile.server";

// Bucket name for avatars
const avatarStorageBucket = "avatars";

// Allowed image file extensions
const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp", "gif"];

// Maximum file size in bytes (5MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

/**
 * Gets the current user from Supabase Auth
 * @returns User object from Supabase Auth
 * @throws Error if user is not authenticated
 */
const getAuthUser = async () => {
  const supabase = createClientComponent();
  const { data, error } = await supabase.auth.getUser();

  if (error || !data.user) {
    throw createErrorObject(
      error?.message || "User not authenticated",
      error?.name,
      401
    );
  }

  return data.user;
};

/**
 * Gets the public URL for an avatar
 * @param path - Storage path for the avatar
 * @returns Public URL for the avatar
 */
export const getAvatarUrl = (path: string) => {
  if (!path) return null;

  // Use the client-side Supabase instance for frontend rendering
  const supabase = createClientComponent();
  return supabase.storage.from(avatarStorageBucket).getPublicUrl(path).data
    .publicUrl;
};

/**
 * Validates an image file for upload
 * @param file - File to validate
 * @throws Error if file is invalid
 */
const validateImageFile = (file: File) => {
  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    throw createErrorObject(
      `File size exceeds maximum allowed (${MAX_FILE_SIZE / 1024 / 1024}MB)`,
      "VALIDATION_ERROR",
      400
    );
  }

  // Check file type
  const fileExt = file.name.split(".").pop()?.toLowerCase();

  if (!fileExt || !ALLOWED_EXTENSIONS.includes(fileExt)) {
    throw createErrorObject(
      `File type not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(", ")}`,
      "VALIDATION_ERROR",
      400
    );
  }
};

/**
 * Uploads a user avatar to Supabase Storage
 * @param file - File to upload
 * @returns Uploaded file path and public URL
 */
export const uploadAvatar = async (file: File) => {
  try {
    // Validate file before proceeding
    validateImageFile(file);

    const user = await getAuthUser();
    const supabase = createClientComponent();

    // Create a unique filename with timestamp to avoid conflicts
    const timestamp = new Date().getTime();
    const fileExt = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const filePath = `${user.id}/${timestamp}.${fileExt}`;

    // Upload the file to Supabase Storage
    const { data, error } = await supabase.storage
      .from(avatarStorageBucket)
      .upload(filePath, file, {
        upsert: true,
        cacheControl: "3600",
      });

    if (error) {
      throw createErrorObject(error.message, "STORAGE_ERROR", 400);
    }

    if (!data || !data.path) {
      throw createErrorObject("Failed to upload file", "STORAGE_ERROR", 500);
    }

    // Get the public URL
    const publicUrl = getAvatarUrl(data.path);

    // Delete previous avatar if it exists using server actions
    try {
      const currentProfile = await getUserProfileAction(user.id);

      if (currentProfile) {
        const oldAvatarPath = currentProfile.user_photo_upload;

        // Delete old avatar if it exists and is different from new path
        if (oldAvatarPath && oldAvatarPath !== data.path) {
          await deleteAvatarFileAction(oldAvatarPath);
        }
      }
    } catch (cleanupError) {
      // Log but don't fail the entire operation if cleanup fails
      console.warn("Failed to clean up old avatar:", cleanupError);
    }

    // Update user profile in database using server action
    const updatedProfile = await updateUserAvatarAction(
      user.id,
      data.path,
      publicUrl
    );

    if (!updatedProfile) {
      throw createErrorObject("Failed to update user profile", "DB_ERROR", 500);
    }

    return {
      path: data.path,
      url: publicUrl,
      profile: updatedProfile,
    };
  } catch (error) {
    console.error("Avatar upload error:", error);
    throw handleRTKError(error);
  }
};

/**
 * Deletes a user's avatar from Supabase Storage
 * @returns Updated user profile
 */
export const deleteAvatar = async () => {
  try {
    const user = await getAuthUser();

    // Get current user profile to find avatar path using server action
    const currentProfile = await getUserProfileAction(user.id);

    if (!currentProfile) {
      throw createErrorObject("User profile not found", "NOT_FOUND", 404);
    }

    const avatarPath = currentProfile.user_photo_upload;

    // Delete from storage if path exists using server action
    if (avatarPath) {
      await deleteAvatarFileAction(avatarPath);
    }

    // Update user profile in database using server action
    const updatedProfile = await updateUserAvatarAction(user.id, null, null);

    if (!updatedProfile) {
      throw createErrorObject("Failed to update user profile", "DB_ERROR", 500);
    }

    return {
      profile: updatedProfile,
    };
  } catch (error) {
    console.error("Avatar deletion error:", error);
    throw handleRTKError(error);
  }
};

export default {
  uploadAvatar,
  deleteAvatar,
  getAvatarUrl,
};
